import { apiClient } from "./apiClient";

const BASE_PATH = "/admin/xgboost";

function snakeToCamel<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
    result[camelKey] = val;
  }
  return result as T;
}

export interface XGBoostOverview {
  enabled: boolean;
  threshold: number;
  mode: string;
  modelVersion: string | null;
  modelRole: string | null;
  trainedAt: string | null;
  challengerAvailable: boolean;
  decisions24h: number;
  passRate24h: number | null;
  totalDecisions: number;
  totalSamples: number;
  trainableSamples: number;
  lastPromotion: string | null;
  lastRetrain: string | null;
  gateHealthy: boolean;
  gateConsecutiveTimeouts: number;
  gateTotalFallbacks: number;
  gateAlertFired: boolean;
}

export interface DecisionEvent {
  id: string;
  createdAt: string;
  symbol: string;
  stage: string;
  score: number | null;
  threshold: number | null;
  passed: boolean | null;
  reason: string | null;
  modelVersion: string | null;
  modelRole: string | null;
  direction: string | null;
  tradeOutcomeId: string | null;
  recommendationId: string | null;
}

export interface DecisionDetail {
  id: string;
  createdAt: string;
  symbol: string;
  marketType: string | null;
  timeframeContext: string | null;
  direction: string | null;
  stage: string;
  recommendationId: string | null;
  tradeOutcomeId: string | null;
  modelRole: string | null;
  modelVersion: string | null;
  modelMode: string | null;
  score: number | null;
  threshold: number | null;
  passed: boolean | null;
  reason: string | null;
  signalsAvailable: boolean | null;
  prefilterResults: Record<string, unknown> | null;
  featuresSnapshot: Record<string, unknown> | null;
  metadataJson: Record<string, unknown> | null;
}

export interface DecisionListResponse {
  decisions: DecisionEvent[];
  count: number;
  total: number;
  page: number;
  pageSize: number;
}

export interface MemorySummary {
  totalSamples: number;
  trainableSamples: number;
  nonTrainable: number;
  recent7d: number;
  recent30d: number;
  bySource: Record<string, number>;
  byRegime: Record<string, number>;
  top_exclusion_reasons?: Array<{ reason: string; count: number }>;
}

export interface SampleItem {
  id: string;
  symbol: string | null;
  sourceType: string;
  outcome: string | null;
  pnlPct: number | null;
  marketRegime: string | null;
  symbolFamily: string | null;
  qualityScore: number | null;
  sampleWeight: number | null;
  isTrainable: boolean;
  createdAt: string | null;
}

export interface SampleListResponse {
  samples: SampleItem[];
  count: number;
}

export interface TrainingRun {
  id: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  requestedBy: string | null;
  sourceSummary: string | null;
  sampleCount: number | null;
  metricsJson: Record<string, unknown> | null;
  candidateVersion: string | null;
  errorMessage: string | null;
}

export interface ModelRegistryItem {
  id: string;
  role: string;
  version: string;
  trainedAt: string | null;
  isActive: boolean;
  isScalpMode: boolean;
  thresholdDefault: number | null;
  metricsJson: Record<string, unknown> | null;
  archivedAt: string | null;
  createdAt: string;
}

export interface BackfillJob {
  id: string;
  status: string;
  marketType: string | null;
  symbolsScope: Record<string, unknown> | null;
  timeframes: string[] | null;
  fromDatetime: string | null;
  toDatetime: string | null;
  requestedSamples: number | null;
  collectedSamples: number | null;
  progressPct: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ShardProgress {
  shardId: number;
  symbolsProcessed: number;
  totalSymbols: number;
  samplesCollected: number;
  currentSymbol: string;
  status: string;
}

export interface BackfillProgress {
  jobId: string;
  jobStatus: string;
  overallPct: number;
  totalSamples: number;
  shards: ShardProgress[];
  elapsedSeconds: number;
  startedAt: string | null;
}

export interface DriftSnapshot {
  id: string;
  computedAt: string;
  windowLabel: string | null;
  featureDriftScore: number | null;
  scoreDistributionDrift: number | null;
  passRateDrift: number | null;
  alertLevel: string | null;
  metadataJson: Record<string, unknown> | null;
}

export interface ActionResult {
  success: boolean;
  message: string;
  id?: string;
}

export async function getOverview(): Promise<XGBoostOverview> {
  const resp = await apiClient.get<Record<string, unknown>>(`${BASE_PATH}/overview`);
  return snakeToCamel<XGBoostOverview>(resp.data);
}

export async function getDecisions(
  page = 1,
  pageSize = 50,
  filters?: { symbol?: string; stage?: string; passed?: boolean; modelVersion?: string; hoursBack?: number; sortBy?: string; sortOrder?: string; dateFrom?: string; dateTo?: string }
): Promise<DecisionListResponse> {
  const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
  if (filters?.symbol) params.symbol = filters.symbol;
  if (filters?.stage) params.stage = filters.stage;
  if (filters?.passed !== undefined) params.passed = filters.passed;
  if (filters?.modelVersion) params.model_version = filters.modelVersion;
  if (filters?.hoursBack) params.hours_back = filters.hoursBack;
  if (filters?.sortBy) params.sort_by = filters.sortBy;
  if (filters?.sortOrder) params.sort_order = filters.sortOrder;
  if (filters?.dateFrom) params.date_from = filters.dateFrom;
  if (filters?.dateTo) params.date_to = filters.dateTo;
  const resp = await apiClient.get<{
    decisions: Record<string, unknown>[];
    count: number;
    total: number;
    page: number;
    page_size?: number;
    pageSize?: number;
  }>(`${BASE_PATH}/decisions`, { params });
  return {
    decisions: resp.data.decisions.map(d => snakeToCamel<DecisionEvent>(d)),
    count: resp.data.count,
    total: resp.data.total,
    page: resp.data.page,
    pageSize: resp.data.page_size ?? resp.data.pageSize ?? pageSize,
  };
}

export async function getDecisionDetail(id: string): Promise<DecisionDetail> {
  const resp = await apiClient.get<Record<string, unknown>>(`${BASE_PATH}/decisions/${id}`);
  return snakeToCamel<DecisionDetail>(resp.data);
}

export async function getMemorySummary(): Promise<MemorySummary> {
  const resp = await apiClient.get<Record<string, unknown>>(`${BASE_PATH}/memory/summary`);
  return snakeToCamel<MemorySummary>(resp.data);
}

export async function getSamples(
  sourceType?: string,
  marketRegime?: string,
  minQuality = 0,
  limit = 100
): Promise<SampleListResponse> {
  const params: Record<string, string | number> = { limit };
  if (sourceType) params.sourceType = sourceType;
  if (marketRegime) params.marketRegime = marketRegime;
  if (minQuality > 0) params.minQuality = minQuality;
  const resp = await apiClient.get<{ samples: Record<string, unknown>[]; count: number }>(`${BASE_PATH}/samples`, { params });
  return {
    count: resp.data.count,
    samples: resp.data.samples.map(s => snakeToCamel<SampleItem>(s)),
  };
}

export async function runCuration(): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/curate`);
  return resp.data;
}

export async function getTrainingRuns(status?: string): Promise<{ runs: TrainingRun[]; count: number }> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const resp = await apiClient.get<{ runs: TrainingRun[]; count: number }>(`${BASE_PATH}/training-runs`, { params });
  return resp.data;
}

export async function triggerTraining(mode = "scalp", refresh = false, notes?: string, modelType = "legacy", maxSamples?: number): Promise<ActionResult> {
  const params: Record<string, string> = { training_mode: mode, refresh: String(refresh), model_type: modelType };
  if (notes) params.notes = notes;
  if (maxSamples !== undefined) params.max_samples = String(maxSamples);
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/training-runs`, null, { params });
  return resp.data;
}

export async function triggerRefresh(mode = "phase", notes?: string): Promise<ActionResult> {
  return triggerTraining(mode, true, notes || "refresh from DB");
}

export async function triggerFullTraining(mode = "scalp"): Promise<ActionResult> {
  return triggerTraining(mode, false, "full training from DB");
}

export async function triggerAutoTrain(days = 1): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/auto-train-now`, null, {
    params: { days },
  });
  return resp.data;
}

export interface TrainingLogs {
  lines: string[];
  totalLines: number;
  eof: boolean;
  status: string;
}

export async function getTrainingLogs(runId: string, afterLine = 0): Promise<TrainingLogs> {
  const params: Record<string, number> = { after_line: afterLine };
  const resp = await apiClient.get<TrainingLogs>(`${BASE_PATH}/training-runs/${runId}/logs`, { params });
  return resp.data;
}

export async function deleteTrainingRun(runId: string): Promise<ActionResult> {
  const resp = await apiClient.delete<ActionResult>(`${BASE_PATH}/training-runs/${runId}`);
  return resp.data;
}

export async function rollbackModel(modelId: string): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/models/${modelId}/rollback`);
  return resp.data;
}

export async function deleteModel(modelId: string): Promise<ActionResult> {
  const resp = await apiClient.delete<ActionResult>(`${BASE_PATH}/models/${modelId}`);
  return resp.data;
}

export async function getModels(): Promise<{ models: ModelRegistryItem[]; count: number }> {
  const resp = await apiClient.get<{ models: ModelRegistryItem[]; count: number }>(`${BASE_PATH}/models`);
  return resp.data;
}

export async function promoteChallenger(modelId: string): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/models/${modelId}/promote`);
  return resp.data;
}

export async function getBackfillJobs(): Promise<{ jobs: BackfillJob[]; count: number }> {
  const resp = await apiClient.get<{ jobs: BackfillJob[]; count: number }>(`${BASE_PATH}/backfill-jobs`);
  return resp.data;
}

export async function getBackfillProgress(jobId: string): Promise<BackfillProgress> {
  const resp = await apiClient.get<BackfillProgress>(`${BASE_PATH}/backfill-jobs/${jobId}/progress`);
  return resp.data;
}

export async function createBackfillJob(data: Record<string, unknown>): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/backfill-jobs`, data);
  return resp.data;
}

export async function createMultiTfBackfill(): Promise<{ success: boolean; message: string; jobs: Record<string, unknown>[] }> {
  const resp = await apiClient.post<{ success: boolean; message: string; jobs: Record<string, unknown>[] }>(`${BASE_PATH}/backfill-jobs/multi-tf`);
  return resp.data;
}

export async function cancelBackfillJob(jobId: string): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/backfill-jobs/${jobId}/cancel`);
  return resp.data;
}

export async function deleteBackfillJob(jobId: string): Promise<ActionResult> {
  const resp = await apiClient.delete<ActionResult>(`${BASE_PATH}/backfill-jobs/${jobId}`);
  return resp.data;
}

export async function getDriftSnapshots(limit = 20): Promise<{ snapshots: DriftSnapshot[]; count: number }> {
  const resp = await apiClient.get<{ snapshots: DriftSnapshot[]; count: number }>(`${BASE_PATH}/drift`, { params: { limit } });
  return resp.data;
}

export async function computeDrift(): Promise<ActionResult> {
  const resp = await apiClient.post<ActionResult>(`${BASE_PATH}/recompute-drift`);
  return resp.data;
}

export async function previewDataset(data: Record<string, unknown>): Promise<Record<string, unknown>> {
  const resp = await apiClient.post<Record<string, unknown>>(`${BASE_PATH}/datasets/preview`, data);
  return resp.data;
}

export interface PerformanceItem {
  symbol: string;
  score: number;
  threshold: number;
  passed: boolean;
  modelVersion: string | null;
  priceAtDecision: number | null;
  priceAfter1h: number | null;
  priceAfter2h: number | null;
  pctChange1h: number | null;
  pctChange2h: number | null;
  direction1h: string | null;
  direction2h: string | null;
  lowestPrice2h: number | null;
  pctLowest2h: number | null;
  decisionTime: string;
}

export interface PerformanceResponse {
  items: PerformanceItem[];
  upCount: number;
  downCount: number;
  sidewaysCount: number;
  avgChange1h: number;
  avgChange2h: number;
  total: number;
  totalQueried: number;
}

export async function getPerformance(
  hoursBack = 48,
  passedFilter = "all",
  directionFilter = "all",
  limit = 20,
  symbol?: string,
  dateFrom?: string,
  dateTo?: string,
  includePrices = false,
): Promise<PerformanceResponse> {
  const params: Record<string, string | number | boolean> = {
    passed_filter: passedFilter,
    direction_filter: directionFilter,
    limit,
    include_prices: includePrices,
  };
  if (dateFrom && dateTo) {
    params.date_from = dateFrom;
    params.date_to = dateTo;
  } else {
    params.hours_back = hoursBack;
  }
  if (symbol) params.symbol = symbol;
  const resp = await apiClient.get<Record<string, unknown>>(`${BASE_PATH}/performance`, {
    params,
    timeout: 120000,
  });
  const data = snakeToCamel<Record<string, unknown>>(resp.data);
  if (Array.isArray(data.items)) {
    data.items = data.items.map((item: unknown) => snakeToCamel<PerformanceItem>(item as Record<string, unknown>));
  }
  return data as unknown as PerformanceResponse;
}

export interface PriceRequestItem {
  symbol: string;
  index: number;
  decisionTimeMs: number;
}

export interface PriceItem {
  index: number;
  symbol: string;
  priceAtDecision: number | null;
  priceAfter1h: number | null;
  priceAfter2h: number | null;
  pctChange1h: number | null;
  pctChange2h: number | null;
  direction1h: string | null;
  direction2h: string | null;
  error: string | null;
}

export interface PriceResponse {
  prices: PriceItem[];
  upCount: number;
  downCount: number;
  sidewaysCount: number;
  avgChange1h: number;
  avgChange2h: number;
}

export async function fetchPrices(
  items: Array<{ symbol: string; index: number; decisionTimeMs: number }>,
): Promise<PriceResponse> {
  const resp = await apiClient.post<Record<string, unknown>>(`${BASE_PATH}/performance/prices`, { items });
  const raw = resp.data as Record<string, unknown>;
  const priceItemsRaw = (raw.prices ?? raw.Prices ?? []) as Array<Record<string, unknown>>;
  const converted = priceItemsRaw.map(p => {
    const item: Record<string, unknown> = {};
    item.index = p.index ?? p.Index;
    item.symbol = p.symbol ?? p.Symbol;
    item.priceAtDecision = p.priceAtDecision ?? p.price_at_decision ?? p.PriceAtDecision;
    item.priceAfter1h = p.priceAfter1h ?? p.price_after_1h ?? p.PriceAfter1h;
    item.priceAfter2h = p.priceAfter2h ?? p.price_after_2h ?? p.PriceAfter2h;
    item.pctChange1h = p.pctChange1h ?? p.pct_change_1h ?? p.PctChange1h;
    item.pctChange2h = p.pctChange2h ?? p.pct_change_2h ?? p.PctChange2h;
    item.direction1h = p.direction1h ?? p.direction_1h ?? p.Direction1h;
    item.direction2h = p.direction2h ?? p.direction_2h ?? p.Direction2h;
    item.error = p.error ?? p.Error;
    return item as unknown as PriceItem;
  });
  return {
    prices: converted,
    upCount: (raw.upCount ?? raw.up_count ?? 0) as number,
    downCount: (raw.downCount ?? raw.down_count ?? 0) as number,
    sidewaysCount: (raw.sidewaysCount ?? raw.sideways_count ?? 0) as number,
    avgChange1h: (raw.avgChange1h ?? raw.avg_change_1h ?? 0) as number,
    avgChange2h: (raw.avgChange2h ?? raw.avg_change_2h ?? 0) as number,
  };
}

export interface AutoTrainingStatus {
  enabled: boolean;
  nextCheckHours: number;
  timeSinceLastRunSeconds: number | null;
  timeSinceLastRunDisplay: string | null;
  lastRetrainTriggered: boolean | null;
  lastRetrainResult: string | null;
  newTradesSinceLastRetrain: number | null;
  retrainThreshold: number | null;
  trainingRuns24h: number | null;
  lastTrainingRun: {
    id: string;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
    sampleCount: number | null;
    candidateVersion: string | null;
  } | null;
  message: string;
}

export function getAutoTrainingStatus(): Promise<AutoTrainingStatus> {
  // Use the existing training-runs API (proven to work with current auth)
  // instead of a dedicated endpoint that may have CORS/auth edge cases
  return getTrainingRuns().then(({ runs, count }) => {
    const latest = runs.length > 0 ? runs[0] : null;
    const now = Date.now();
    const runs24h = runs.filter(r => {
      const started = r.startedAt ? new Date(r.startedAt).getTime() : 0;
      return now - started < 24 * 3600 * 1000;
    }).length;

    let timeSinceDisplay: string | null = null;
    let timeSinceSec: number | null = null;
    let message = "Auto-training status derived from latest training runs.";

    if (latest && latest.completedAt) {
      const elapsed = (now - new Date(latest.completedAt).getTime()) / 1000;
      timeSinceSec = elapsed;
      if (elapsed < 60) timeSinceDisplay = `${Math.round(elapsed)}s ago`;
      else if (elapsed < 3600) timeSinceDisplay = `${Math.round(elapsed / 60)}m ago`;
      else if (elapsed < 86400) timeSinceDisplay = `${Math.round(elapsed / 3600)}h ago`;
      else timeSinceDisplay = `${Math.round(elapsed / 86400)}d ago`;

      if (latest.status === "completed") {
        message = `Last training completed ${timeSinceDisplay} (v: ${latest.candidateVersion || "?"}).`;
      } else if (latest.status === "running") {
        message = `Training in progress... started ${timeSinceDisplay}.`;
      } else if (latest.status === "failed") {
        message = `Last training failed ${timeSinceDisplay}. Check logs.`;
      }
    } else if (!latest) {
      message = "No training runs found. Auto-training not yet triggered.";
      timeSinceDisplay = "never";
    }

    return {
      enabled: true,
      nextCheckHours: 6,
      timeSinceLastRunSeconds: timeSinceSec,
      timeSinceLastRunDisplay: timeSinceDisplay,
      lastRetrainTriggered: latest?.status === "completed" || latest?.status === "running",
      lastRetrainResult: latest?.status ?? null,
      newTradesSinceLastRetrain: null,
      retrainThreshold: null,
      trainingRuns24h: runs24h,
      lastTrainingRun: latest ? {
        id: latest.id,
        status: latest.status,
        startedAt: latest.startedAt,
        completedAt: latest.completedAt,
        sampleCount: latest.sampleCount,
        candidateVersion: latest.candidateVersion,
      } : null,
      message,
    };
  });
}
